/**
 * Cache Service for translation results
 * Implements Requirements 13.1, 13.2
 * 
 * Provides local storage caching with 24-hour TTL for translation results
 */

import { TranslationRequest, TranslationResponse } from '../types';

interface CacheEntry {
  request: TranslationRequest;
  response: TranslationResponse;
  timestamp: number;
  expiresAt: number;
}

interface CacheStats {
  totalEntries: number;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
}

class CacheService {
  private readonly CACHE_KEY_PREFIX = 'translayshun_cache_';
  private readonly CACHE_STATS_KEY = 'translayshun_cache_stats';
  private readonly DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
  private readonly MAX_CACHE_SIZE = 100; // Maximum number of cached translations

  /**
   * Generate a cache key from a translation request
   */
  private generateCacheKey(request: TranslationRequest): string {
    const key = JSON.stringify({
      source: request.source.trim().toLowerCase(),
      sourceLang: request.sourceLang,
      targetLang: request.targetLang,
      context: request.context,
      sourceGender: request.genderPreferences.sourceGender,
      targetGender: request.genderPreferences.targetGender,
    });
    
    // Use a simple hash to keep keys manageable
    return this.CACHE_KEY_PREFIX + this.hashString(key);
  }

  /**
   * Simple string hash function
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Get cached translation if available and not expired
   */
  get(request: TranslationRequest): TranslationResponse | null {
    try {
      const cacheKey = this.generateCacheKey(request);
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        this.updateStats('miss');
        return null;
      }

      const entry: CacheEntry = JSON.parse(cached);
      const now = Date.now();

      // Check if cache entry has expired
      if (now > entry.expiresAt) {
        localStorage.removeItem(cacheKey);
        this.updateStats('miss');
        return null;
      }

      // Update response metadata to indicate it's from cache
      const response: TranslationResponse = {
        ...entry.response,
        metadata: {
          ...entry.response.metadata,
          cached: true,
          timestamp: new Date(entry.timestamp),
        },
      };

      this.updateStats('hit');
      return response;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  }

  /**
   * Store translation result in cache
   */
  set(request: TranslationRequest, response: TranslationResponse, ttlMs?: number): void {
    try {
      const cacheKey = this.generateCacheKey(request);
      const now = Date.now();
      const ttl = ttlMs || this.DEFAULT_TTL_MS;

      const entry: CacheEntry = {
        request,
        response,
        timestamp: now,
        expiresAt: now + ttl,
      };

      // Check cache size and evict oldest entries if needed
      this.evictIfNeeded();

      localStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (error) {
      console.error('Error writing to cache:', error);
      // If localStorage is full, try to clear old entries
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.clearExpired();
        try {
          const cacheKey = this.generateCacheKey(request);
          const now = Date.now();
          const ttl = ttlMs || this.DEFAULT_TTL_MS;
          const entry: CacheEntry = {
            request,
            response,
            timestamp: now,
            expiresAt: now + ttl,
          };
          localStorage.setItem(cacheKey, JSON.stringify(entry));
        } catch (retryError) {
          console.error('Failed to cache after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Check if a translation is cached
   */
  has(request: TranslationRequest): boolean {
    return this.get(request) !== null;
  }

  /**
   * Clear all cached translations
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(this.CACHE_KEY_PREFIX) || key === this.CACHE_STATS_KEY) {
          localStorage.removeItem(key);
        }
      });
      this.resetStats();
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  /**
   * Clear expired cache entries
   */
  clearExpired(): void {
    try {
      const now = Date.now();
      const keys = Object.keys(localStorage);
      let clearedCount = 0;

      keys.forEach((key) => {
        if (key.startsWith(this.CACHE_KEY_PREFIX)) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const entry: CacheEntry = JSON.parse(cached);
              if (now > entry.expiresAt) {
                localStorage.removeItem(key);
                clearedCount++;
              }
            }
          } catch (error) {
            // Remove corrupted entries
            localStorage.removeItem(key);
            clearedCount++;
          }
        }
      });

      if (clearedCount > 0) {
        console.log(`Cleared ${clearedCount} expired cache entries`);
      }
    } catch (error) {
      console.error('Error clearing expired cache:', error);
    }
  }

  /**
   * Evict oldest entries if cache size exceeds maximum
   */
  private evictIfNeeded(): void {
    try {
      const entries: Array<{ key: string; timestamp: number }> = [];
      const keys = Object.keys(localStorage);

      keys.forEach((key) => {
        if (key.startsWith(this.CACHE_KEY_PREFIX)) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const entry: CacheEntry = JSON.parse(cached);
              entries.push({ key, timestamp: entry.timestamp });
            }
          } catch (error) {
            // Remove corrupted entries
            localStorage.removeItem(key);
          }
        }
      });

      // If we're over the limit, remove oldest entries
      if (entries.length >= this.MAX_CACHE_SIZE) {
        entries.sort((a, b) => a.timestamp - b.timestamp);
        const toRemove = entries.slice(0, entries.length - this.MAX_CACHE_SIZE + 1);
        toRemove.forEach((entry) => {
          localStorage.removeItem(entry.key);
        });
        console.log(`Evicted ${toRemove.length} old cache entries`);
      }
    } catch (error) {
      console.error('Error evicting cache entries:', error);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    try {
      const statsJson = localStorage.getItem(this.CACHE_STATS_KEY);
      if (!statsJson) {
        return {
          totalEntries: this.getCacheSize(),
          cacheHits: 0,
          cacheMisses: 0,
          hitRate: 0,
        };
      }

      const stats = JSON.parse(statsJson);
      return {
        totalEntries: this.getCacheSize(),
        cacheHits: stats.hits || 0,
        cacheMisses: stats.misses || 0,
        hitRate: this.calculateHitRate(stats.hits || 0, stats.misses || 0),
      };
    } catch (error) {
      console.error('Error reading cache stats:', error);
      return {
        totalEntries: 0,
        cacheHits: 0,
        cacheMisses: 0,
        hitRate: 0,
      };
    }
  }

  /**
   * Get current cache size
   */
  private getCacheSize(): number {
    try {
      const keys = Object.keys(localStorage);
      return keys.filter((key) => key.startsWith(this.CACHE_KEY_PREFIX)).length;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Calculate hit rate percentage
   */
  private calculateHitRate(hits: number, misses: number): number {
    const total = hits + misses;
    return total > 0 ? Math.round((hits / total) * 100) : 0;
  }

  /**
   * Update cache statistics
   */
  private updateStats(type: 'hit' | 'miss'): void {
    try {
      const statsJson = localStorage.getItem(this.CACHE_STATS_KEY);
      const stats = statsJson ? JSON.parse(statsJson) : { hits: 0, misses: 0 };

      if (type === 'hit') {
        stats.hits = (stats.hits || 0) + 1;
      } else {
        stats.misses = (stats.misses || 0) + 1;
      }

      localStorage.setItem(this.CACHE_STATS_KEY, JSON.stringify(stats));
    } catch (error) {
      console.error('Error updating cache stats:', error);
    }
  }

  /**
   * Reset cache statistics
   */
  private resetStats(): void {
    try {
      localStorage.setItem(
        this.CACHE_STATS_KEY,
        JSON.stringify({ hits: 0, misses: 0 })
      );
    } catch (error) {
      console.error('Error resetting cache stats:', error);
    }
  }

  /**
   * Get estimated cache size in bytes
   */
  getEstimatedSize(): number {
    try {
      let totalSize = 0;
      const keys = Object.keys(localStorage);

      keys.forEach((key) => {
        if (key.startsWith(this.CACHE_KEY_PREFIX)) {
          const value = localStorage.getItem(key);
          if (value) {
            // Rough estimate: 2 bytes per character in UTF-16
            totalSize += (key.length + value.length) * 2;
          }
        }
      });

      return totalSize;
    } catch (error) {
      console.error('Error calculating cache size:', error);
      return 0;
    }
  }

  /**
   * Format bytes to human-readable string
   */
  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}

// Export singleton instance
export const cacheService = new CacheService();
